import { Action, Subject } from "@aitisal/contracts/common";
import { getRestaurantDashboard } from "@aitisal/sdk/restaurant";
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  Loader,
  Skeleton,
  Tabs,
  Text,
  Tooltip,
  rem,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconBuilding,
  IconChartBar,
  IconMaximize,
  IconMinimize,
  IconRefresh,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { fetchUserAttributes } from "aws-amplify/auth";
import { useEffect, useState } from "react";
import { getTitle } from "../helper/helper";
import { useAbilityStore } from "../stores/rolesRightsStore";
import { useAppShellLayout, useLoading, useRestaurant } from "../stores/stores";
import colors from "../styles/colors";

export const Route = createFileRoute("/")({
  component: HomeComponent,
});

function HomeComponent() {
  const { restaurant, restaurantId } = useRestaurant();
  const { setIsLoading } = useLoading();
  const { setSidebarOpen, setIsSidebarCollapsed } = useAppShellLayout();

  const [selectedTab, setSelectedTab] = useState<any>("get-started");
  const [iframeUrl, setIframeUrl] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const [userName, setUserName] = useState<string>("");

  async function getUserName() {
    const user: any = await fetchUserAttributes();
    setUserName(user?.name);
  }

  const { ability } = useAbilityStore();

  useEffect(() => {
    getUserName();
    setSidebarOpen(0);
    setIsLoading(true);
    if (ability && typeof ability.can === "function") {
      if (!ability.can(Action.Read, Subject.Report)) {
        setSelectedTab("get-started");
        setIsLoading(false);
      } else {
        setIsDashboardLoading(true);
        setDashboardError(null);
        getRestaurantDashboard(restaurantId)
          ?.then((response: any) => {
            setIframeUrl(response?.data?.iframeUrl);
            setIsLoading(false);
            setIsDashboardLoading(false);
          })
          ?.catch((error: any) => {
            setDashboardError("Failed to load dashboard. Please try again.");
            setIsLoading(false);
            setIsDashboardLoading(false);
          });
      }
    } else {
      setSelectedTab("get-started");
      setIsLoading(false);
    }
  }, [restaurantId, ability]);

  return (
    <main className="w-full overflow-x-hidden">
      <div className="flex justify-start items-center gap-2 m-4">
        <div className="p-2 rounded-md border border-solid border-gray-200 h-[50px] w-[50px] flex justify-center items-center">
          <IconBuilding size={30} color={"grey"} />
        </div>
        <div className="flex flex-col p-4">
          <Text
            fz={18}
            fw={600}
            className="flex items-center gap-1"
            component="span"
          >
            Hello, {userName ? userName : <Skeleton width={150} height={20} />}
          </Text>
          <Text c={"grey"} fz={13} fw={"bold"}>
            {getTitle(restaurant?.name)}
          </Text>
        </div>
      </div>
      <Tabs
        color={colors.secondary}
        radius="md"
        defaultValue={selectedTab}
        value={selectedTab}
        onChange={(e) => setSelectedTab(e)}
        style={{ width: "100%" }}
        className="w-full"
      >
        <Tabs.List bg={"#fff"} right={0}>
          <Tabs.Tab value="get-started" pb={rem(10)}>
            <Text fz="15px">Get Started</Text>
          </Tabs.Tab>
          {ability &&
            typeof ability.can === "function" &&
            ability.can(Action.Read, Subject.Report) && (
              <Tabs.Tab value="dashboard" pb={rem(10)}>
                <Text fz="15px">Dashboard</Text>
              </Tabs.Tab>
            )}
        </Tabs.List>
        <Tabs.Panel value="get-started" className={`relative py-5 pb-5 px-5`}>
          <div
            className="flex flex-col justify-start items-start gap-1 p-20 rounded-md"
            style={{ background: colors?.dimSecondary }}
          >
            <Text fz={22} fw={"bolder"}>
              Welcome to Aitisal
            </Text>
            <Text fz="16px" fw={500}>
              Get started by navigating to the sections.
            </Text>
          </div>
        </Tabs.Panel>
        <Tabs.Panel
          value="dashboard"
          className={`relative ${isFullscreen ? "p-0" : "py-5 px-5"}`}
        >
          <div
            className={`w-full ${
              isFullscreen
                ? "h-screen fixed inset-0 z-[9999] bg-white"
                : "h-[calc(100vh-200px)]"
            }`}
          >
            {/* Dashboard Header */}
            <Card
              shadow="sm"
              padding="md"
              radius="md"
              withBorder
              className={`mb-4 ${isFullscreen ? "m-4" : ""}`}
            >
              <Group justify="space-between" align="center">
                <Group align="center" gap="sm">
                  <IconChartBar size={24} color={colors.secondary} />
                  <div>
                    <Text size="lg" fw={600}>
                      Analytics Dashboard
                    </Text>
                    <Text size="sm" c="dimmed">
                      Real-time insights and reports
                    </Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <Tooltip label="Refresh Dashboard">
                    <ActionIcon
                      variant="light"
                      color="blue"
                      onClick={() => {
                        setIframeLoaded(false);
                        setDashboardError(null);
                        const iframe = document.querySelector("iframe");
                        if (iframe) iframe.src = iframe.src;
                      }}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip
                    label={
                      isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"
                    }
                  >
                    <ActionIcon
                      variant="light"
                      color="blue"
                      onClick={() => {
                        const newFullscreenState = !isFullscreen;
                        setIsFullscreen(newFullscreenState);
                        // Collapse sidebar when entering fullscreen, expand when exiting
                        setIsSidebarCollapsed(newFullscreenState);
                      }}
                    >
                      {isFullscreen ? (
                        <IconMinimize size={16} />
                      ) : (
                        <IconMaximize size={16} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Card>

            {/* Dashboard Content */}
            <Card
              shadow="sm"
              padding={0}
              radius="md"
              withBorder
              className={`${isFullscreen ? "mx-4 mb-4" : ""} overflow-hidden`}
              style={{
                height: isFullscreen
                  ? "calc(100vh - 120px)"
                  : "calc(100vh - 280px)",
              }}
            >
              {dashboardError ? (
                <div className="flex items-center justify-center h-full p-8">
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Dashboard Error"
                    color="red"
                    variant="light"
                    className="max-w-md"
                  >
                    <Text size="sm" mb="md">
                      {dashboardError}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      color="red"
                      onClick={() => {
                        setDashboardError(null);
                        setIsDashboardLoading(true);
                        getRestaurantDashboard(restaurantId)
                          ?.then((response: any) => {
                            setIframeUrl(response?.data?.iframeUrl);
                            setIsDashboardLoading(false);
                          })
                          ?.catch(() => {
                            setDashboardError(
                              "Failed to load dashboard. Please try again."
                            );
                            setIsDashboardLoading(false);
                          });
                      }}
                    >
                      Try Again
                    </Button>
                  </Alert>
                </div>
              ) : isDashboardLoading ? (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <Loader size="lg" color={colors.secondary} />
                  <Text size="sm" c="dimmed">
                    Loading dashboard...
                  </Text>
                </div>
              ) : iframeUrl ? (
                <div className="relative w-full h-full">
                  {!iframeLoaded && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10">
                      <Loader size="lg" color={colors.secondary} />
                      <Text size="sm" c="dimmed" mt="md">
                        Loading reports...
                      </Text>
                    </div>
                  )}
                  <iframe
                    src={iframeUrl}
                    width="100%"
                    height="100%"
                    className="border-none"
                    onLoad={() => setIframeLoaded(true)}
                    onError={() => {
                      setDashboardError("Failed to load dashboard content.");
                      setIframeLoaded(true);
                    }}
                    style={{
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <IconChartBar size={48} color="#ccc" className="mb-4" />
                  <Text size="lg" fw={500} mb="xs">
                    Dashboard Not Available
                  </Text>
                  <Text size="sm" c="dimmed" mb="md">
                    The analytics dashboard is currently unavailable. Please
                    contact support if this issue persists.
                  </Text>
                  <Button
                    variant="light"
                    color={colors.secondary}
                    onClick={() => {
                      setIsDashboardLoading(true);
                      getRestaurantDashboard(restaurantId)
                        ?.then((response: any) => {
                          setIframeUrl(response?.data?.iframeUrl);
                          setIsDashboardLoading(false);
                        })
                        ?.catch(() => {
                          setDashboardError(
                            "Failed to load dashboard. Please try again."
                          );
                          setIsDashboardLoading(false);
                        });
                    }}
                  >
                    Retry Loading
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </Tabs.Panel>
      </Tabs>
    </main>
  );
}
